/**
 * Gate de la vista "Lectura de planos" (WIP, DWG/DXF → HP, Fase 0).
 *
 * Se muestra SOLO si se cumplen las DOS condiciones:
 *  1. El flag de despliegue del front `VITE_PLANO_A_HP_ENABLED` está prendido
 *     (en prod va vacío/apagado). Espeja al `PLANO_A_HP_ENABLED` del backend,
 *     que con off devuelve 404 en las rutas de planos.
 *  2. El usuario es STAFF de Ecosoft (`useIsEcosoftStaff`). /planos es una
 *     herramienta interna, no de cliente. El backend además exige `requireStaff()`
 *     en TODAS las rutas de planos, así que esto es la mitad visual del gate.
 *
 * Esconder el ítem/pantalla es cortesía; el portero real es el backend (flag +
 * `requireStaff` + `withTenantDb`). Ver `docs/spec-plano-dwg-a-hp.md` §9.
 */
import { useIsEcosoftStaff } from '../stores/authStore.js';

/**
 * ¿Está prendido el flag del front? Se acepta solo un puñado de valores
 * "verdaderos" a propósito: `Boolean('false')` es `true`, así que no basta con
 * castear el string del env (el placeholder del `.env.example` va vacío).
 */
export const PLANO_A_HP_ENABLED: boolean = ['1', 'true', 'on', 'yes'].includes(
  String(import.meta.env.VITE_PLANO_A_HP_ENABLED ?? '').trim().toLowerCase(),
);

/**
 * ¿Usar el visor CAD nuevo (mlightcad, WebGL) en vez del render SVG para los DWG/DXF? Flag de
 * DESPLIEGUE independiente (default off): con off, /planos usa el SVG de siempre. Se lee igual que
 * el flag de arriba (no basta castear el string: `Boolean('false')` es `true`). El visor solo
 * aplica a planos CAD 2D; el IFC sigue con su visor 3D pase lo que pase.
 */
export const PLANO_VIEWER_MLIGHTCAD: boolean = ['1', 'true', 'on', 'yes'].includes(
  String(import.meta.env.VITE_PLANO_VIEWER_MLIGHTCAD ?? '').trim().toLowerCase(),
);

/**
 * «Conceptos por tipo» (memoria de tipos de ci): flag de despliegue del front. Espeja MEMORIA_TIPOS_ENABLED del
 * API (con off, sus rutas dan 404). Apagado = el armado se comporta exactamente como antes (sin consultas nuevas).
 */
export const MEMORIA_TIPOS_ENABLED: boolean = ['1', 'true', 'on', 'yes'].includes(
  String(import.meta.env.VITE_MEMORIA_TIPOS_ENABLED ?? '').trim().toLowerCase(),
);

/**
 * «Revisar el dibujo» (diagnóstico de DWG/DXF antes de presupuestar): flag de despliegue del front. Apagado no lee ni
 * calcula nada (la pastilla del chequeo queda como siempre).
 */
export const DIAGNOSTICO_DIBUJO_ENABLED: boolean = ['1', 'true', 'on', 'yes'].includes(
  String(import.meta.env.VITE_DIAGNOSTICO_DIBUJO_ENABLED ?? '').trim().toLowerCase(),
);

/**
 * «Revisar el modelo» (diagnóstico de IFC antes de presupuestar): flag de despliegue del front, aparte del de «Revisar el dibujo». Apagado
 * no calcula nada y la pastilla del chequeo del modelo queda como siempre.
 */
export const DIAGNOSTICO_MODELO_ENABLED: boolean = ['1', 'true', 'on', 'yes'].includes(
  String(import.meta.env.VITE_DIAGNOSTICO_MODELO_ENABLED ?? '').trim().toLowerCase(),
);

/** ¿Se ve la vista de lectura de planos? Flag del front + staff Ecosoft. */
export function usePlanoAHpVisible(): boolean {
  const esStaff = useIsEcosoftStaff();
  return PLANO_A_HP_ENABLED && esStaff;
}
